import {Component} from '@angular/core';
import {Validator} from '../utils/Validator';
import {Toaster, Type} from '../toaster/Toaster';
import {UserService} from '../service/user.service';
import {Step} from '../application-flow/steps/step';
import {FormGroupUtil} from '../utils/FormGroupUtil';
import {ActivatedRoute, Router} from '@angular/router';
import {BadRequestError} from '../errors/BadRequestError';
import {AnalyticsTracker} from '../utils/AnalyticsTracker';
import {Keys, StorageService} from '../service/storage.service';
import {EntityConflictError} from '../errors/EntityConflictError';
import {UnprocessableEntityError} from '../errors/UnprocessableEntityError';
import {PanVerificationStep} from '../application-flow/steps/pan-verification.step';
import {ApplicationFlowNavigator} from '../application-flow/application-flow.navigator';
import {LoanApplicationFlowNavigator} from '../application-flow/loan-application-flow.navigator';
import {AbstractApplicationFlowComponent} from '../application-flow/abstract-application-flow.component';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';

export class PanVerificationComponent extends AbstractApplicationFlowComponent {
    public backLink: string;
    public isPanVerified = false;
    public shouldShowLoader: boolean;
    public panFormGroup: FormGroup;
    public readonly formBuilder: FormBuilder;

    private readonly router: Router;
    private readonly validator: Validator;
    protected readonly userService: UserService;
    protected readonly storageService: StorageService;
    private readonly activatedRoute: ActivatedRoute;

    constructor(formBuilder: FormBuilder, validator: Validator, userService: UserService, storageService: StorageService,
                router: Router, activatedRoute: ActivatedRoute, public analyticsTracker: AnalyticsTracker, navigator: ApplicationFlowNavigator) {
        super(navigator);
        this.router = router;
        this.validator = validator;
        this.userService = userService;
        this.formBuilder = formBuilder;
        this.activatedRoute = activatedRoute;
        this.storageService = storageService;
        this.panFormGroup = this.formBuilder.group({
            name: new FormControl(undefined, [Validators.required, Validators.maxLength(50), (control: AbstractControl): ValidationErrors => {
                return this.validateName(control);
            }]),
            pan: new FormControl(undefined, [Validators.required, Validators.pattern(/^([a-zA-Z]){5}([0-9]){4}([a-zA-Z])$/)])
        });
        activatedRoute.data.subscribe(data => {
            if (data['panInformation']) {
                this.onPanInformationReceived(data.panInformation);
            }
            if (data['backLink']) {
                this.backLink = data.backLink;
            }
        });
    }

    public errorMessages() {
        return {
            name: [{
                type: 'required',
                message: 'Please enter your name'
            }, {
                type: 'maxlength',
                message: 'Please abbreviate your name to a maximum of 50 characters'
            }, {
                type: 'pattern',
                message: 'Please enter a valid name'
            }, {
                type: 'not-a-match',
                message: 'The name does not match with the given PAN'
            }],
            pan: [{
                type: 'required',
                message: 'Please enter your PAN'
            }, {
                type: 'pattern',
                message: 'Please enter a valid PAN'
            }, {
                type: 'pan-already-exists',
                message: 'This PAN is already registered. Please login to proceed.'
            }, {
                type: 'invalid-pan-number',
                message: 'Please enter valid pan number'
            }]
        };
    }

    public validateName(control: AbstractControl): ValidationErrors {
        if (control.value && !(this.validator.isValidName(control.value))) {
            return {pattern: 'Please enter a valid name.'};
        }
    }

    public async onSubmit(formGroup: FormGroup): Promise<void> {
        if (this.isPanVerified) {
            this.showLoader();
            await this.onPanVerified();
            this.hideLoader();
            return;
        }

        FormGroupUtil.validateFormGroup(formGroup);
        if (!formGroup.invalid) {
            try {
                this.showLoader();
                const enteredPan = formGroup.controls.pan.value;
                const enteredName = formGroup.controls.name.value;
                const phoneVerificationRequestToken = await this.userService.verifyPan(enteredPan, enteredName);
                this.storageService.saveItem(Keys.NAME, enteredName);
                this.storageService.saveObjectToSession(Keys.PAN, enteredPan);
                this.storageService.saveItem(Keys.PAN_VERIFIED, true.toString());
                this.storageService.saveObjectToSession(Keys.PHONE_VERIFICATION_REQUEST_TOKEN, phoneVerificationRequestToken);
                await this.onPanVerified();
            } catch (error) {
                if (error instanceof EntityConflictError) {
                    this.onPanAlreadyPresent(formGroup);
                } else if (error instanceof BadRequestError) {
                    formGroup.controls.pan.setErrors({'invalid-pan-number': 0});
                    this.analyticsTracker.trackError(this.getErrorMessage(formGroup.controls.pan));
                } else if (error instanceof UnprocessableEntityError) {
                    formGroup.controls.name.setErrors({'not-a-match': 0});
                    this.analyticsTracker.trackError(this.getErrorMessage(formGroup.controls.name));
                } else {
                    Toaster.toast(`Something went wrong. Please try again later.`, Type.Error);
                    throw error;
                }
            } finally {
                this.hideLoader();
            }
        }
    }

    public showLoader(): void {
        this.shouldShowLoader = true;
    }

    public hideLoader(): void {
        this.shouldShowLoader = false;
    }

    public onPanInformationReceived(panInformation: { pan: string, name: string } | null) {
        if (panInformation) {
            this.panFormGroup.controls.name.setValue(panInformation.name);
            this.panFormGroup.controls.name.disable();
            this.panFormGroup.controls.pan.setValue(panInformation.pan);
            this.panFormGroup.controls.pan.disable();
            this.isPanVerified = true;
        }
    }

    isPanNameNotMatched(control: AbstractControl) {
        const errors = control.errors;
        return errors ? 'not-a-match' in errors : false;
    }

    async nextPage(): Promise<void> {
        await this.router.navigate(['../phone-verification'], {relativeTo: this.activatedRoute});
    }

    protected async onPanVerified() {
        await this.nextPage();
    }

    protected onPanAlreadyPresent(formGroup: FormGroup) {
        formGroup.controls.pan.setErrors({'pan-already-exists': 0});
        this.analyticsTracker.trackError(this.getErrorMessage(formGroup.controls.pan));
        this.router.navigate(['../phone-verification'], {relativeTo: this.activatedRoute});
    }

    async previousPage(): Promise<void> {
        if (this.backLink) {
            await this.router.navigate([this.backLink]);
        } else {
            return super.previousPage();
        }
    }

    step(): new(...args: any[]) => Step {
        return PanVerificationStep;
    }
}

@Component({
    templateUrl: './pan-verification.html',
    styleUrls: ['../loan-application-flow-component/application-flow.css']
})
export class LoanApplicationPanVerificationComponent extends PanVerificationComponent {
    constructor(formBuilder: FormBuilder, validator: Validator, userService: UserService, storageService: StorageService,
                router: Router, activatedRoute: ActivatedRoute, analyticsTracker: AnalyticsTracker, navigator: LoanApplicationFlowNavigator) {
        super(formBuilder, validator, userService, storageService, router, activatedRoute, analyticsTracker, navigator);
    }
}

@Component({
    templateUrl: './pan-verification.html',
    styleUrls: ['../loan-application-flow-component/application-flow.css']
})
export class InvestmentApplicationPanVerificationComponent extends PanVerificationComponent {
    constructor(formBuilder: FormBuilder, validator: Validator, userService: UserService, storageService: StorageService,
                router: Router, activatedRoute: ActivatedRoute, analyticsTracker: AnalyticsTracker) {
        super(formBuilder, validator, userService, storageService, router, activatedRoute, analyticsTracker, null);
    }
}
